<script setup lang="tsx">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { MethodImportNameMismatch as NameMismatch } from '@/models/api-generated'
import { DoNotImport, validStatusOptions } from './importUtils'

const props = defineProps<{
	totalFoundCount: number
	matchedNoChangesCount: number
	matchedChangesCount: number
	unmatchedCount: number
	notFound: Array<{ name: string; reason: string }>
	nameMismatches: NameMismatch[]
	noAssetCount: number
	noBpCount: number
	responsibleNotFoundCount: number
	missingResponsibleLabels: string[]
	highSecNeedCount: number
	highSecDeselected: boolean
	hasAnyVersionMismatch: boolean
	approvedVersionMismatch: boolean
	statusMismatchCount: number
	statusReplacementMap: Record<string, string>
	uniqueInvalidStatuses: string[]
	invalidStatusCounts: Record<string, number>
	selectedCount: number
	missingRequirementsCount: number
	readyToImportCount: number
}>()

const emit = defineEmits<{
	'toggle-high-sec-deselect': []
	'update:approvedVersionMismatch': [val: boolean]
	'set-status-replacement': [status: string, value: string]
}>()

const { t } = useI18n()
const showMissingResponsibles = ref(false)
const showSecNeedWarning = ref(false)
const showApproveVersionWarning = ref(false)
const showStatusPicker = ref(false)

function onHighSecClick() {
	if (!props.highSecDeselected) showSecNeedWarning.value = true
	else emit('toggle-high-sec-deselect')
}

function onVersionMismatchClick() {
	if (!props.approvedVersionMismatch) showApproveVersionWarning.value = true
	else emit('update:approvedVersionMismatch', false)
}
</script>

<template>
	<div class="card border-secondary-subtle mb-3 mt-2">
		<div class="card-header py-2 px-3 bg-body-tertiary border-secondary-subtle text-center">
			<span class="fw-semibold">{{ t('import.importSummary') }}</span>
		</div>
		<div class="card-body py-2 px-3 bg-body-tertiary rounded d-flex flex-column gap-2">
			<div class="d-flex flex-wrap gap-2 align-items-center">
				<span class="small fw-semibold text-secondary">{{ t('import.detected') }}</span>
				<span class="px-2 py-1 rounded-pill bg-secondary-subtle border border-secondary-subtle text-secondary small d-flex align-items-center gap-1">
					<i class="bi bi-list-check"></i>
					<strong>{{ totalFoundCount }}</strong> {{ t('import.found') }}
				</span>
				<span v-if="matchedNoChangesCount" class="px-2 py-1 rounded-pill bg-secondary-subtle border border-secondary-subtle text-secondary small d-flex align-items-center gap-1">
					<i class="bi bi-check2"></i>
					<strong>{{ matchedNoChangesCount }}</strong> {{ t('import.noChanges') }}
				</span>
				<span v-if="matchedChangesCount" class="px-2 py-1 rounded-pill bg-info-subtle border border-info-subtle text-info small d-flex align-items-center gap-1">
					<i class="bi bi-pencil-square"></i>
					<strong>{{ matchedChangesCount }}</strong> {{ t('import.withChanges') }}
				</span>
				<span v-if="unmatchedCount" class="px-2 py-1 rounded-pill bg-primary-subtle border border-primary-subtle text-primary small d-flex align-items-center gap-1">
					<i class="bi bi-plus-circle"></i>
					<strong>{{ unmatchedCount }}</strong> {{ t('import.importNew') }}
				</span>
				<span v-if="notFound.length" class="px-2 py-1 rounded-pill bg-danger-subtle border border-danger-subtle text-danger small d-flex align-items-center gap-1">
					<i class="bi bi-x-circle"></i>
					<strong>{{ notFound.length }}</strong> {{ t('import.importNotFound') }}
				</span>
			</div>

			<div class="d-flex flex-column gap-2 pt-2 border-top border-secondary-subtle">
				<span class="small fw-semibold text-secondary">{{ t('import.attentionRequired') }}</span>
				<div
					v-if="noAssetCount || noBpCount || highSecNeedCount || highSecDeselected || hasAnyVersionMismatch || statusMismatchCount || responsibleNotFoundCount"
					class="d-flex flex-column gap-2"
				>
					<div v-if="noAssetCount || noBpCount || responsibleNotFoundCount" class="d-flex flex-wrap gap-2 align-items-center">
						<span v-if="noAssetCount" class="badge bg-danger">
							<i class="bi bi-clipboard me-1"></i>
							<strong>{{ noAssetCount }}</strong> {{ t('import.assetMissing') }}
						</span>
						<span v-if="noBpCount" class="badge bg-danger">
							<i class="bi bi-kanban me-1"></i>
							<strong>{{ noBpCount }}</strong> {{ t('import.bpMissing') }}
						</span>
						<div v-if="responsibleNotFoundCount" class="d-flex flex-column gap-1 ms-auto">
							<button
								type="button"
								class="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
								@click="showMissingResponsibles = !showMissingResponsibles"
							>
								<i class="bi bi-person-x"></i>
								<strong>{{ responsibleNotFoundCount }}</strong> {{ t('import.responsibleNotFound') }}
								<i :class="showMissingResponsibles ? 'bi bi-chevron-up' : 'bi bi-chevron-down'" class="ms-1 small"></i>
							</button>
							<div v-if="showMissingResponsibles" class="d-flex flex-column gap-1 mt-1">
								<span v-for="label in missingResponsibleLabels" :key="label" class="badge bg-secondary-subtle border border-secondary-subtle text-secondary fw-normal">
									<i class="bi bi-person me-1"></i>{{ label }}
								</span>
							</div>
						</div>
					</div>

					<div v-if="highSecNeedCount || highSecDeselected" class="d-flex flex-column gap-1">
						<div class="d-flex align-items-center gap-2">
							<span class="badge bg-warning text-dark">
								<i class="bi bi-shield-exclamation me-1"></i>
								<strong>{{ highSecNeedCount }}</strong> {{ t('import.secNeedWarning') }}
							</span>
							<button
								type="button"
								:class="highSecDeselected ? 'btn btn-sm btn-warning ms-auto' : 'btn btn-sm btn-outline-warning ms-auto'"
								@click="onHighSecClick"
							>
								{{ t('import.deselectSecurityWarnings') }}
								<i v-if="highSecDeselected" class="bi bi-check-lg ms-1"></i>
							</button>
						</div>
						<div v-if="showSecNeedWarning" class="alert alert-info py-2 px-2 small mb-0 d-flex flex-column gap-2 mw-warning-panel">
							<div><i class="bi bi-info-circle-fill me-1"></i>{{ t('import.deselectSecurityWarningsInfo') }}</div>
							<div class="d-flex gap-2">
								<button
									type="button"
									class="btn btn-sm btn-warning"
									@click="emit('toggle-high-sec-deselect'); showSecNeedWarning = false"
								>
									{{ t('misc.confirm') }}
								</button>
								<button type="button" class="btn btn-sm btn-light" @click="showSecNeedWarning = false">
									{{ t('misc.cancel') }}
								</button>
							</div>
						</div>
					</div>

					<div v-if="hasAnyVersionMismatch" class="d-flex flex-column gap-1">
						<div class="d-flex align-items-center gap-2">
							<span class="badge bg-warning text-dark">
								<i class="bi bi-file-earmark-diff me-1"></i>
								<strong>{{ nameMismatches.length }}</strong> {{ t('import.versionMismatch') }}
							</span>
							<button
								type="button"
								:class="approvedVersionMismatch ? 'btn btn-sm btn-warning ms-auto' : 'btn btn-sm btn-outline-warning ms-auto'"
								@click="onVersionMismatchClick"
							>
								{{ t('import.approveVersionMismatch') }}
								<i v-if="approvedVersionMismatch" class="bi bi-check-lg ms-1"></i>
							</button>
						</div>
						<div v-if="showApproveVersionWarning" class="alert alert-warning py-2 px-2 small mb-0 d-flex flex-column gap-2 mw-warning-panel">
							<div><i class="bi bi-exclamation-triangle-fill me-1"></i>{{ t('import.approveVersionMismatchWarning') }}</div>
							<div class="d-flex gap-2">
								<button
									type="button"
									class="btn btn-sm btn-warning"
									@click="emit('update:approvedVersionMismatch', true); showApproveVersionWarning = false"
								>
									{{ t('misc.confirm') }}
								</button>
								<button type="button" class="btn btn-sm btn-light" @click="showApproveVersionWarning = false">
									{{ t('misc.cancel') }}
								</button>
							</div>
						</div>
					</div>

					<div v-if="statusMismatchCount" class="d-flex flex-column gap-1">
						<div class="d-flex align-items-center gap-2">
							<span class="badge bg-danger">
								<i class="bi bi-arrow-repeat me-1"></i>
								<strong>{{ statusMismatchCount }}</strong> {{ t('import.statusMismatch') }}
							</span>
							<span class="text-danger small ms-auto">* {{ t('import.required') }}</span>
							<button
								type="button"
								:class="showStatusPicker ? 'btn btn-sm btn-danger' : 'btn btn-sm btn-outline-danger'"
								@click="showStatusPicker = !showStatusPicker"
							>
								{{ t('import.chooseStatusReplacement') }}
								<span v-if="Object.keys(statusReplacementMap).some((k) => statusReplacementMap[k])" class="badge bg-light text-dark ms-1">
									{{ Object.keys(statusReplacementMap).filter((k) => statusReplacementMap[k]).length }}/{{ uniqueInvalidStatuses.length }}
								</span>
							</button>
						</div>
						<div v-if="showStatusPicker" class="d-flex flex-column gap-1 mt-1">
							<div v-for="s in uniqueInvalidStatuses" :key="s" class="d-flex align-items-center gap-2">
								<span :class="statusReplacementMap[s] === DoNotImport ? 'badge bg-secondary text-nowrap' : 'badge bg-danger text-nowrap'">
									{{ s || '—' }} ({{ invalidStatusCounts[s] ?? 0 }})
								</span>
								<i class="bi bi-arrow-right text-muted small"></i>
								<select
									:value="statusReplacementMap[s]"
									class="form-select form-select-sm status-select"
									@change="emit('set-status-replacement', s, ($event.target as HTMLSelectElement).value)"
								>
									<option value="">{{ t('misc.select') }}…</option>
									<option v-for="opt in validStatusOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
									<option :value="DoNotImport">{{ t('import.dontImport') }}</option>
								</select>
								<i v-if="statusReplacementMap[s] && statusReplacementMap[s] !== DoNotImport" class="bi bi-check-circle-fill text-success small"></i>
								<i v-else-if="statusReplacementMap[s] === DoNotImport" class="bi bi-x-circle-fill text-danger small"></i>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div class="d-flex flex-column gap-2 pt-2 border-top border-secondary-subtle">
				<span class="small fw-semibold text-secondary">{{ t('import.importPlan') }}</span>
				<div class="d-flex flex-column align-items-center gap-1">
					<span class="px-2 py-1 rounded-pill bg-secondary-subtle border border-secondary-subtle text-secondary small d-flex align-items-center gap-1">
						<i class="bi bi-check2-square"></i>
						<strong>{{ selectedCount }}</strong> {{ t('import.selected') }}
					</span>
					<template v-if="missingRequirementsCount">
						<i class="bi bi-arrow-down text-muted small"></i>
						<span class="px-2 py-1 rounded-pill bg-warning-subtle border border-warning-subtle text-warning small d-flex align-items-center gap-1">
							<i class="bi bi-exclamation-circle"></i>
							<strong>{{ missingRequirementsCount }}</strong> {{ t('import.missingRequirements') }}
						</span>
					</template>
					<i class="bi bi-arrow-down text-muted small"></i>
					<span class="px-2 py-1 rounded-pill bg-success-subtle border border-success-subtle text-success small d-flex align-items-center gap-1">
						<i class="bi bi-check-circle-fill"></i>
						<strong>{{ readyToImportCount }}</strong> {{ t('import.readyToImport') }}
					</span>
				</div>
			</div>
		</div>
	</div>

	<details v-if="notFound.length" class="mt-2">
		<summary class="text-danger small cursor-pointer">{{ t('misc.none') }} ({{ notFound.length }})</summary>
		<ul class="list-unstyled mt-2 ps-3">
			<li v-for="nf in notFound" :key="nf.name" class="small text-muted">
				<i class="bi bi-x-circle text-danger me-1"></i>{{ nf.name }} — {{ nf.reason }}
			</li>
		</ul>
	</details>

	<details v-if="nameMismatches.length" class="mt-2">
		<summary class="text-warning small cursor-pointer">
			<i class="bi bi-exclamation-triangle me-1"></i>
			{{ nameMismatches.length }} {{ t('import.nameMismatchWarning') }}
		</summary>
		<ul class="list-unstyled mt-2 ps-3">
			<li v-for="mm in nameMismatches" :key="mm.name" class="small mb-2">
				<span class="fw-semibold">{{ mm.name }}</span>
				<div class="text-muted d-flex align-items-center gap-1 flex-wrap">
					<span class="text-truncate mw-name-mismatch">{{ mm.inFile }}</span>
					<i class="bi bi-arrow-right mx-1 flex-shrink-0"></i>
					<span class="text-truncate mw-name-mismatch">{{ mm.inDB }}</span>
				</div>
			</li>
		</ul>
	</details>
</template>

<style lang="sass" scoped>
.status-select
	width: auto

.mw-warning-panel
	max-width: 420px

.mw-name-mismatch
	max-width: 280px
</style>
